/**
 * classroomRoyaleHandoff
 *
 * A coach creates a Puzzle Royale from INSIDE the classroom (a different tab),
 * but a royale lobby is born only from the `fun_room_create` socket message
 * which the royale lobby panel owns. To keep the classroom tab intact we open
 * the Fun Challenges lobby in a NEW tab and have THAT tab create the lobby.
 *
 * The new tab needs two things the URL shouldn't carry verbatim: the chosen
 * race settings (a whole object) and the classroom meeting/occurence IDs (so it
 * can POST back to Django to register the royale + notify students). We stash
 * them in localStorage under a one-shot key (same origin, survives the new-tab
 * cold load) and pass only that key in the URL: `?royaleAutoCreate=<key>`.
 *
 * The reader consumes-and-deletes so a refresh of the new tab can't re-create a
 * second lobby.
 */

import type {RoyaleSettings} from '../royaleTypes';

const PREFIX = 'ccRoyaleHandoff:';

export interface ClassroomRoyaleHandoff {
  settings: RoyaleSettings;
  meetingId: string;
  occurenceId: string;
}

/** Stash a handoff and return the one-shot key to put in the new-tab URL. */
export function stashClassroomRoyaleHandoff(
  payload: ClassroomRoyaleHandoff,
): string | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  // No Date.now()/Math.random() constraints here (plain web runtime), but keep
  // the key collision-resistant without them by combining time + a counter.
  const key = `${PREFIX}${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
  try {
    window.localStorage.setItem(key, JSON.stringify(payload));
    return key;
  } catch {
    return null;
  }
}

/** Read-and-delete a stashed handoff. Returns null if missing/invalid. */
export function consumeClassroomRoyaleHandoff(
  key: string,
): ClassroomRoyaleHandoff | null {
  if (typeof window === 'undefined' || !window.localStorage || !key) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    window.localStorage.removeItem(key);
    const parsed = JSON.parse(raw) as ClassroomRoyaleHandoff;
    if (!parsed || !parsed.settings || !parsed.meetingId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
